// Central place for frontend-only UI visibility rules. Backend authorization
// (app/models/ability.rb) is the source of truth for what actions are actually
// allowed; these flags only decide what's shown, so add new gates here instead
// of re-deriving role checks in individual pages.
import { usePage } from "@inertiajs/react";

import type { CurrentUser, SharedProps } from "@/types";

export type Permissions = {
  isAdmin: boolean;
  // "Administradora de Casa" — scoped to their own house's courses.
  isHouseManager: boolean;
  // Has any backoffice role (admin, director "Encargado", subscriber "Inscriptor",
  // center director, house_manager "Administradora de Casa", house_master). Anonymous visitors and students are not staff.
  isBackofficeStaff: boolean;
  // Self-enrollment (courses and wait lists) is a student/public-only action —
  // no backoffice profile enrolls or wait-lists itself, only other participants.
  canSelfEnroll: boolean;
  // Edit/delete for Users, Categories, Centers, Courses — admin only per Ability.
  canManageAdminOnlyRecords: boolean;
  // Edit/delete House — admin and house_master per Ability (unlike the other
  // catalog resources above, house_master also gets :update/:destroy on House).
  canManageHouses: boolean;
  // "Editar curso" link on Course show — admin and house_master can update a
  // Course; house_manager/director/subscriber cannot.
  canEditCourse: boolean;
  // Managing another person's enrollment/wait-list entry on their behalf:
  // adding one ("Agregar inscripción" / "Agregar a lista de espera"), payment,
  // destroy, confirm-attendance. Admin, house_master, and director/subscriber
  // (for their own courses) have this; house_manager only has read/update_diet
  // on Enrollment and read on WaitList — no create/add_payment/destroy/confirm.
  canManageParticipants: boolean;
  // Per-action gates on participants (Encargado vs Inscriptor split):
  // abonos — admin, house_master, Encargado (is_director).
  canAddPayment: boolean;
  // confirm attendance, delete an enrollment, add enrollments — admin,
  // house_master, Inscriptor (is_subscriber).
  canConfirmEnrollment: boolean;
  canDeleteEnrollment: boolean;
  canEnrollParticipants: boolean;
  // Excel export of participants — admin, house_master, Encargado.
  canExportParticipants: boolean;
  // Inscripciones/Lista de espera tables + Excel export on Course show — same
  // roles as canManageParticipants (excludes house_manager, who gets a diet
  // summary card instead, see Courses/Show.tsx). Named for what it gates so it
  // doesn't silently drift if either concern changes independently.
  canViewParticipantTables: boolean;
  // "Respuestas de cierre" box on Course show — director/subscriber always see
  // it (even before the closing questionnaire is answered), same as admin and
  // house_master. house_manager never sees it, regardless of answers.
  canViewClosingQuestionnaire: boolean;
  // Destroy on an existing wait list entry — admin and house_master have
  // :manage WaitList; house_manager/director/subscriber only have read access
  // to wait lists (director/subscriber can still create one via
  // canManageParticipants, just not destroy an existing one).
  canManageWaitList: boolean;
  // Force-enroll a wait list entry past capacity/category restrictions
  // ("Forzar inscripción") — admin only per Ability; house_master's :manage
  // WaitList is explicitly carved out with `cannot :promote, WaitList`,
  // since this is an exceptional action, not routine wait-list management.
  canForceEnrollFromWaitList: boolean;
  canViewAdminCourseFilters: boolean;
  // "Tipos de cursos" nav link + Categories pages — admin only per Ability.
  canViewCategoriesNav: boolean;
  canViewPastCourses: boolean;
  canExportCourses: boolean;
  canViewStatisticsNav: boolean;
  canManageUsersNav: boolean;
  canViewAsistenciaNav: boolean;
  // "Centros" / "Casas" nav links — every staff role except a Director de
  // Centro on its own (they only get courses + the attendance report).
  canViewCentersNav: boolean;
  canViewHousesNav: boolean;
  // "Ver perfil" link on a participant's email in Course show — admin and
  // director/subscriber ("Encargada de actividad") per Ability's `can
  // :profile, User` in `grant_staff_read_abilities`. house_master/house_manager
  // don't get it, even though they can see the same participant tables.
  canViewUserProfiles: boolean;
};

export function computePermissions(
  user: CurrentUser,
  viewingAsStudent = false,
): Permissions {
  if (viewingAsStudent && user) {
    return computePermissions({
      ...user,
      is_admin: false,
      is_director: false,
      is_house_manager: false,
      is_house_master: false,
      is_subscriber: false,
      is_center_director: false,
    });
  }

  const isAdmin = !!user?.is_admin;
  const isHouseMaster = !!user?.is_house_master;
  const isHouseManager = !!user?.is_house_manager;
  const isDirector = !!user?.is_director;
  const isSubscriber = !!user?.is_subscriber;
  const isCenterDirector = !!user?.is_center_director;
  const isOnlyCenterDirector =
    isCenterDirector &&
    !(isAdmin || isDirector || isSubscriber || isHouseManager || isHouseMaster);
  const isBackofficeStaff =
    isAdmin ||
    isDirector ||
    isHouseManager ||
    isHouseMaster ||
    isSubscriber ||
    isCenterDirector;

  return {
    isAdmin,
    isHouseManager,
    isBackofficeStaff,
    canSelfEnroll: !isBackofficeStaff,
    canManageAdminOnlyRecords: isAdmin,
    canManageHouses: isAdmin || isHouseMaster,
    canEditCourse: isAdmin || isHouseMaster,
    canManageParticipants:
      isAdmin || isHouseMaster || isDirector || isSubscriber,
    canAddPayment: isAdmin || isHouseMaster || isDirector,
    canConfirmEnrollment: isAdmin || isHouseMaster || isSubscriber,
    canDeleteEnrollment: isAdmin || isHouseMaster || isSubscriber,
    canEnrollParticipants: isAdmin || isHouseMaster || isSubscriber,
    canExportParticipants: isAdmin || isHouseMaster || isDirector,
    canViewParticipantTables:
      isAdmin ||
      isHouseMaster ||
      isDirector ||
      isSubscriber ||
      isCenterDirector,
    canViewClosingQuestionnaire:
      isAdmin || isHouseMaster || isDirector || isSubscriber,
    canManageWaitList: isAdmin || isHouseMaster,
    canForceEnrollFromWaitList: isAdmin,
    canViewAdminCourseFilters: isAdmin || isDirector || isCenterDirector,
    canViewCategoriesNav: isAdmin,
    canViewPastCourses: isAdmin,
    canExportCourses: isAdmin,
    canViewStatisticsNav: isAdmin,
    canManageUsersNav: isAdmin,
    canViewCentersNav: !isOnlyCenterDirector,
    canViewHousesNav: !isOnlyCenterDirector,
    canViewAsistenciaNav: isAdmin || isCenterDirector,
    canViewUserProfiles:
      isAdmin || isDirector || isSubscriber || isCenterDirector,
  };
}

export function usePermissions(): Permissions {
  const { currentUser, viewingAsStudent } = usePage<SharedProps>().props;
  return computePermissions(currentUser, viewingAsStudent);
}

// Close a Course — admin, or the Encargado (is_director) it's assigned to
// (`course.user_id`), per Ability's `can :close, Course, user_id: user.id`.
// Course-scoped, unlike the flags above, since ownership varies per record.
export function canCloseCourse(
  user: CurrentUser,
  course: { user_id: number | null },
): boolean {
  if (!user) return false;
  if (user.is_admin) return true;

  return !!user.is_director && course.user_id === user.id;
}

// Reopen a Course — admin only per Ability; no other role has :reopen.
export function canReopenCourse(user: CurrentUser): boolean {
  return !!user?.is_admin;
}
